import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#initArtworkTilt();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#artworkController?.abort();
    this.#resetArtwork();
    this.#controller.abort();
  }

  // Pointer lighting belongs only to the main product artwork, never to cards or the zoom dialog.
  #artworkController;
  #artworkQuery;
  #artworkSurface;
  #artworkBounds;
  #artworkPoint;
  #artworkFrame = 0;

  #initArtworkTilt() {
    if (!this.matches('.tb81-artwork') || !this.closest('.product-information__media')) return;
    this.#artworkController?.abort();
    this.#resetArtwork();
    this.#artworkController = new AbortController();
    const options = { signal: this.#artworkController.signal, passive: true };
    this.#artworkQuery = matchMedia('(min-width:750px) and (hover:hover) and (pointer:fine) and (prefers-reduced-motion:no-preference)');
    this.#artworkQuery.addEventListener('change', this.#resetArtwork, options);
    this.addEventListener('pointerover', this.#enterArtwork, options);
    this.addEventListener('pointermove', this.#moveArtwork, options);
    this.addEventListener('pointerout', this.#leaveArtwork, options);
    this.addEventListener('pointerleave', this.#resetArtwork, options);
    this.addEventListener('click', (event) => { if (event.button === 0) this.#resetArtwork(); }, options);
    window.addEventListener('resize', this.#resetArtwork, options);
    window.addEventListener('scroll', this.#resetArtwork, { ...options, capture: true });
  }

  /** @param {PointerEvent} event */
  #enterArtwork = (event) => {
    if (!this.#artworkQuery?.matches || event.pointerType !== 'mouse' || event.buttons) return;
    const image = event.target instanceof Element ? event.target.closest('.product-media-container--image') : null;
    if (!image || image.closest('zoom-dialog')) return;
    const surface = image.parentElement?.matches('.media-gallery__grid') ? image : image.closest('slideshow-container');
    if (!surface || surface.closest('media-gallery') !== this || surface === this.#artworkSurface) return;
    this.#resetArtwork();
    this.#artworkSurface = surface;
    this.#artworkBounds = surface.getBoundingClientRect();
    surface.classList.add('tb83-tilting');
    this.#moveArtwork(event);
  };

  /** @param {PointerEvent} event */
  #moveArtwork = (event) => {
    if (!this.#artworkQuery?.matches || event.pointerType !== 'mouse' || event.buttons) return;
    if (!this.#artworkSurface) { this.#enterArtwork(event); return; }
    const bounds = this.#artworkBounds;
    if (!bounds.width || !bounds.height) return;
    this.#artworkPoint = {
      x: Math.max(-1, Math.min(1, (event.clientX - bounds.left) / bounds.width * 2 - 1)),
      y: Math.max(-1, Math.min(1, (event.clientY - bounds.top) / bounds.height * 2 - 1)),
    };
    if (this.#artworkFrame) return;
    // At most one update per pointer frame; no idle animation loop or layout read here.
    this.#artworkFrame = requestAnimationFrame(() => {
      this.#artworkFrame = 0;
      const surface = this.#artworkSurface;
      if (!surface || !this.#artworkQuery.matches) return;
      const { x, y } = this.#artworkPoint;
      surface.style.setProperty('--tb83-rotate-x', (-y * 1.8).toFixed(2) + 'deg');
      surface.style.setProperty('--tb83-rotate-y', (x * 2.5).toFixed(2) + 'deg');
      surface.style.setProperty('--tb83-reflection-x', (50 + x * 7).toFixed(2) + '%');
      surface.style.setProperty('--tb83-reflection-y', (50 + y * 5).toFixed(2) + '%');
      surface.style.setProperty('--tb83-shadow-x', (-x * 2).toFixed(2) + 'px');
      surface.style.setProperty('--tb83-shadow-y', (14 + Math.abs(y) * 1.5).toFixed(2) + 'px');
    });
  };

  /** @param {PointerEvent} event */
  #leaveArtwork = (event) => {
    if (event.relatedTarget instanceof Node && this.#artworkSurface?.contains(event.relatedTarget)) return;
    this.#resetArtwork();
  };

  #resetArtwork = () => {
    cancelAnimationFrame(this.#artworkFrame);
    this.#artworkFrame = 0;
    const surface = this.#artworkSurface;
    if (surface) {
      surface.classList.remove('tb83-tilting');
      ['rotate-x', 'rotate-y', 'reflection-x', 'reflection-y', 'shadow-x', 'shadow-y'].forEach(name => {
        surface.style.removeProperty('--tb83-' + name);
      });
    }
    this.#artworkSurface = null;
    this.#artworkBounds = null;
    this.#artworkPoint = null;
  };

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
